import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../../globals';
import { SelectedSiteService } from '../../selected-site.service';
import { Site, SiteService } from '../site';
import { ForceCacheResponseJSON, ForceCacheState } from './cache.model';

@Injectable()
export class CacheService {
  private headers = new HttpHeaders().set('Content-Type', 'application/json');
  private siteId: string;
  private forceCacheState$$: BehaviorSubject<ForceCacheState> = new BehaviorSubject<ForceCacheState>(
    {} as ForceCacheState,
  );
  private readonly host: string;

  constructor(
    private http: HttpClient,
    private selectedSiteService: SelectedSiteService,
    private siteService: SiteService,
  ) {
    this.host =
      environment !== 'prod' && environment !== 'production'
        ? 'website-pro-demo-cache-manager.vendasta-internal.com'
        : 'website-pro-prod-cache-manager.vendasta-internal.com';
    this.selectedSiteService.getSiteId().subscribe((siteId: string) => (this.siteId = siteId));
  }

  private static handleHttpError(error: any): Observable<any> {
    return throwError(error.message || error);
  }

  public flushCache(): Observable<boolean> {
    return this.flushCachePost().pipe(
      map(() => true),
      catchError((err) => CacheService.handleHttpError(err)),
    );
  }

  flushCachePost(): Observable<any> {
    const url = `/api/v2/site/${this.siteId}/cache/purge/`;
    return this.http.post(url, {}, { headers: this.headers });
  }

  public getForceCacheState$(): Observable<ForceCacheState> {
    return this.forceCacheState$$.asObservable();
  }

  setForceCache(enabled: boolean): Observable<any> {
    return this.postForceCache(enabled).pipe(
      map(
        (resp) => {
          this.forceCacheState$$.next(ForceCacheState.fromJSON(resp.data));
        },
        catchError((err) => {
          return CacheService.handleHttpError(err);
        }),
      ),
    );
  }

  /**
   * TODO: WP-860 - this feature was disabled, but leaving the code here in case we want to refine it in the future
   */
  getForceCache(): Observable<any> {
    let params = new HttpParams();
    const url = this.getIgnoreCacheHeaderUrl();
    return this.siteService.get().pipe(
      switchMap((site: Site) => {
        params = params.set('partner_id', site.partnerId).set('account_group_id', site.accountId);
        return this.http.get<ForceCacheResponseJSON>(url, {
          headers: this.headers,
          params,
          withCredentials: true,
        });
      }),
      map(
        (resp) => this.forceCacheState$$.next(ForceCacheState.fromJSON(resp.data)),
        catchError((err) => CacheService.handleHttpError(err)),
      ),
    );
  }

  hostWithScheme(): string {
    const scheme = 'https://';
    return scheme + this.host;
  }

  private postForceCache(enabled: boolean): Observable<ForceCacheResponseJSON> {
    let params = new HttpParams();
    const body = { enabled: enabled };
    const url = this.getIgnoreCacheHeaderUrl();
    return this.siteService.get().pipe(
      switchMap((site: Site) => {
        params = params.set('partner_id', site.partnerId).set('account_group_id', site.accountId);
        return this.http.post<ForceCacheResponseJSON>(url, body, {
          headers: this.headers,
          params,
          withCredentials: true,
        });
      }),
      catchError((err) => CacheService.handleHttpError(err)),
    );
  }

  private getIgnoreCacheHeaderUrl(): string {
    return this.hostWithScheme() + '/api/v1/site/' + this.siteId + '/ignore-cache-headers/';
  }
}
